import React, { useMemo, useState } from "react"
import {
	Icons,
	Box,
	Text,
	Stack,
	Inline,
	Grid,
	Col,
	Badge,
	joinAttributes,
	Ping,
} from "@sembark-travel/ui-base"
import { formatDate, parseDate, getDiff } from "@sembark-travel/datetime-utils"
import {
	ITrip,
	ITripQuote,
	IQuoteHotelParams,
	IQuoteCab,
	IQuoteTravelActivity,
} from "./store"
import * as styles from "./ItineraryStreamPreview.css"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"

function getRelativeDayNum(date: Date | string, startDate: Date) {
	const checkInDate = parseDate(date)
	const start = parseDate(startDate)
	const diffDays = getDiff(checkInDate, start, "days")
	return diffDays + 1
}

function getRelativeDayLabel(
	date: Date | string,
	startDate: Date,
	suffix = "Day"
) {
	const checkInDate = parseDate(date)
	const dayNum = getRelativeDayNum(date, startDate)
	return `${withOrdinalSuffix(dayNum)} ${suffix} (${formatDate(checkInDate, "ddd DD MMM")})`
}

interface ItineraryStreamPreviewProps {
	streamQuote: ITripQuote | undefined
	startDate: Date
	days: number
	trip: ITrip
	streamLabel?: string | null
	completedSections: { section: string; day?: number }[]
}

export function ItineraryStreamPreview({
	streamQuote,
	startDate,
	days,
	trip,
	streamLabel,
	completedSections,
}: ItineraryStreamPreviewProps) {
	const { counts, allHotels, combinedServices } = useMemo(() => {
		if (!streamQuote) {
			return {
				counts: { hotels: 0, cabs: 0, activities: 0 },
				allHotels: [],
				combinedServices: [],
			}
		}

		const primaryHotels = (streamQuote.quote?.hotels || []).map((h) => ({
			...h,
			optionName: streamQuote.name || "Primary Option",
		}))
		const alternativeHotels = (streamQuote.options || []).flatMap((opt) => {
			return (opt.quote?.hotels || []).map((h) => ({
				...h,
				optionName: opt.name,
			}))
		})

		const rawHotels = [...primaryHotels, ...alternativeHotels]

		// Grouping map
		const groupedMap = new Map<
			string,
			IQuoteHotelParams[number] & { optionName?: string }
		>()
		rawHotels.forEach((rawH) => {
			const h = rawH as unknown as {
				hotel?: { id?: string | number; name?: string }
				room_type?: { id?: string | number; name?: string }
				optionName?: string
				dates?: Date[]
				date?: string
			} & IQuoteHotelParams[number]
			const hotelId = h.hotel?.id || h.hotel?.name || "unknown-hotel"
			const roomId = h.room_type?.id || h.room_type?.name || "unknown-room"
			const optName = h.optionName || streamQuote.name || "Primary Option"
			const groupKey = `${hotelId}-${roomId}-${optName}`

			const itemDates: Date[] = []
			if (h.dates && h.dates.length > 0) {
				h.dates.forEach((d: Date) => itemDates.push(new Date(d)))
			} else if (h.date) {
				itemDates.push(new Date(h.date))
			}

			if (groupedMap.has(groupKey)) {
				const existing = groupedMap.get(groupKey)
				if (existing) {
					// Merge dates, filtering out duplicates
					const mergedDates = [...(existing.dates || [])]
					const existingTimeStrings = mergedDates.map((d: Date) =>
						d.toDateString()
					)

					itemDates.forEach((d: Date) => {
						if (!existingTimeStrings.includes(d.toDateString())) {
							mergedDates.push(d)
						}
					})

					existing.dates = mergedDates
				}
			} else {
				// Clone to avoid mutation of store state
				groupedMap.set(groupKey, {
					...h,
					optionName: optName,
					dates: itemDates,
				} as IQuoteHotelParams[number] & { optionName?: string })
			}
		})

		// Sort the dates inside each grouped hotel so stay nights checkboxes display in chronological order
		groupedMap.forEach((val) => {
			if (val.dates) {
				val.dates.sort((a: Date, b: Date) => a.getTime() - b.getTime())
			}
		})

		// Convert back to array and sort chronologically by the first check-in date
		const allHotelsList = Array.from(groupedMap.values()).sort((a, b) => {
			const ad = a.dates?.[0] ? a.dates[0].getTime() : 0
			const bd = b.dates?.[0] ? b.dates[0].getTime() : 0
			return ad - bd
		})

		const allCabs = streamQuote.quote?.cabs || []
		const allActivities = streamQuote.quote?.travel_activities || []

		const combinedServicesList = [
			...allCabs.map((c) => ({
				type: "cab" as const,
				date: c.date,
				timestamp: c.date ? new Date(c.date).getTime() : 0,
				data: c,
			})),
			...allActivities.map((a) => ({
				type: "activity" as const,
				date: a.date,
				timestamp: a.date ? new Date(a.date).getTime() : 0,
				data: a,
			})),
		].sort((a, b) => a.timestamp - b.timestamp)

		const countsObj = {
			hotels: rawHotels.length,
			cabs: allCabs.length,
			activities: allActivities.length,
		}

		return {
			counts: countsObj,
			allHotels: allHotelsList,
			combinedServices: combinedServicesList,
		}
	}, [streamQuote])

	const displayedHotels = allHotels

	const progressChecklistItems = useMemo(() => {
		const items = []
		const completedTags = new Map()
		completedSections.forEach((item) => {
			if (item.section === "day") {
				completedTags.set(item.day, "Completed")
			} else {
				completedTags.set("hotel", "Completed")
			}
		})
		items.push({
			title: "Searching Hotels",
			subtitle: completedTags.get("hotel") || "Pending",
			icon: Icons.Bed,
			sectionId: "section-hotels",
		})

		for (let i = 1; i <= days; i++) {
			items.push({
				title: `Day ${i} Transport & Activities`,
				subtitle: completedTags.get(i) || "Pending",
				icon: Icons.Bus,
				sectionId: `section-day-${i}`,
			})
		}

		return items
	}, [days, completedSections])

	const [selectedSectionFilter, setSelectedSectionFilter] = useState<
		string | null
	>(null)
	return (
		<Box paddingTop="2">
			<Stack
				paddingX="2"
				gap={"2"}
				className={`${styles.previewThemeClass} ${styles.itineraryPreviewRoot}`}
			>
				<Box
					borderRadius="lg"
					className={styles.itineraryPreviewHeader}
					flexShrink="0"
				>
					<Stack gap={"2"} alignItems="start">
						<Inline
							className={styles.glassBadgeVt}
							borderRadius="full"
							padding="1"
							alignItems="center"
							gap="2"
						>
							<Ping color="danger" title="DRAFTING" />
							<Text fontSize="sm" fontWeight="semibold" color="white" as="span">
								Crafting Custom Itinerary
							</Text>
						</Inline>
						<Text as="p" fontSize="sm">
							The AI is currently building your travel plan day-by-day. Watch as
							it curates hotel options, transport routes, and activities in
							real-time. Once the draft is complete, the pricing and
							customization form will load automatically.
						</Text>

						<Inline
							gap="4"
							flexWrap="wrap"
							paddingTop="2"
							borderTopWidth="1"
							className={styles.glassDividerVt}
						>
							<Inline
								gap="2"
								alignItems="center"
								className={styles.glassBadgeVt}
								borderRadius="full"
								paddingX="3"
								paddingY="1"
								fontSize="xs"
							>
								<Text as="strong" color="white">
									Start Date:
								</Text>
								{formatDate(startDate, "MMM DD, YYYY")}
							</Inline>
							<Inline
								gap="2"
								alignItems="center"
								className={styles.glassBadgeVt}
								borderRadius="full"
								paddingX="3"
								paddingY="1"
								fontSize="xs"
							>
								<Text as="strong" color="white">
									Duration:
								</Text>{" "}
								{`${days - 1}N ${days}D`}
							</Inline>
							{trip.no_of_adults > 0 && (
								<Inline
									gap="2"
									alignItems="center"
									className={styles.glassBadgeVt}
									borderRadius="full"
									paddingX="3"
									paddingY="1"
									fontSize="xs"
								>
									<Text as="strong" color="white">
										Guests:
									</Text>{" "}
									{trip.no_of_adults} Adults
									{trip.children?.length
										? `, ${trip.children.length} Children`
										: ""}
								</Inline>
							)}
							{(counts.hotels > 0 ||
								counts.cabs > 0 ||
								counts.activities > 0) && (
								<Inline
									gap="2"
									alignItems="center"
									className={styles.glassBadgeVt}
									borderRadius="full"
									paddingX="3"
									paddingY="1"
									fontSize="xs"
								>
									<Text as="strong" color="white">
										Drafted:
									</Text>{" "}
									{counts.hotels} Hotels, {counts.cabs} Transports,{" "}
									{counts.activities} Activities
								</Inline>
							)}
						</Inline>
					</Stack>
				</Box>

				{/* Body Layout: Left Sidebar + Right Content */}
				<Box
					display="flex"
					flexDirection={{ xs: "col", lg: "row" }}
					gap="4"
					alignItems="start"
					flex={{ xs: "none", lg: "1" }}
					minHeight="0"
				>
					<Box
						width={{ xs: "full", lg: "1/4" }}
						flexShrink="0"
						height={{ xs: "auto", lg: "full" }}
						overflow="hidden"
					>
						<Box
							backgroundColor="white"
							borderRadius="lg"
							boxShadow="lg"
							padding="4"
							height="full"
							overflowY="auto"
						>
							<Text
								as="h3"
								fontSize="sm"
								fontWeight="semibold"
								color="subtle"
								style={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
							>
								GENERATION STEPS
							</Text>
							<Stack marginTop="4">
								{progressChecklistItems.map((item, index) => {
									const isCompleted = item.subtitle === "Completed"
									const isPending = item.subtitle === "Pending"
									const isLast = index === progressChecklistItems.length - 1

									return (
										<Box
											key={item.sectionId}
											onClick={() => {
												if (isPending) return
												if (item.sectionId) {
													setSelectedSectionFilter((prev) =>
														prev === item.sectionId ? null : item.sectionId
													)
												} else {
													setSelectedSectionFilter(null)
												}
											}}
											cursor={isPending ? "default" : "pointer"}
											aria-disabled={isPending}
										>
											<Inline
												className={styles.aiChecklistItemVt}
												gap="4"
												alignItems="center"
												paddingY={isPending ? "4" : "2"}
												paddingX={isPending ? "4" : "0"}
												backgroundColor={
													selectedSectionFilter !== null &&
													selectedSectionFilter !== item.sectionId
														? "subtle"
														: isPending
															? "primary1"
															: "transparent"
												}
												borderRadius="lg"
												borderWidth={isPending ? "1" : "0"}
												borderColor={
													isPending ? "primary_emphasis" : "transparent"
												}
											>
												<Inline
													alignItems="center"
													justifyContent="center"
													width="10"
													height="10"
													borderRadius={isPending ? "lg" : "full"}
													flexShrink="0"
													backgroundColor={
														isCompleted
															? "success"
															: isPending
																? "primary"
																: "white"
													}
													borderWidth={isPending ? "1" : "0"}
													borderColor={isPending ? "muted" : "transparent"}
												>
													{isCompleted ? (
														<Icons.Ok color="success" size="5" />
													) : isPending ? (
														<Box className={styles.aiChecklistSpinnerVt} />
													) : (
														<Box color="muted">
															<item.icon size="5" />
														</Box>
													)}
												</Inline>
												<Stack gap="1">
													<Text
														as="span"
														fontSize="base"
														fontWeight="semibold"
														color={
															selectedSectionFilter !== null &&
															selectedSectionFilter !== item.sectionId
																? "subtle"
																: isCompleted
																	? "success"
																	: isPending
																		? "primary"
																		: "muted"
														}
													>
														{item.title}
													</Text>
													<Text
														as="span"
														fontSize="xs"
														color={
															isCompleted
																? "muted"
																: isPending
																	? "primary"
																	: "muted"
														}
													>
														{item.subtitle}
													</Text>
												</Stack>
											</Inline>
											{!isLast && (
												<Box
													width="0"
													height="6"
													borderLeftWidth="2"
													borderColor="muted"
													style={{ borderStyle: "dashed" }}
													marginLeft="4"
													marginY="1"
												/>
											)}
										</Box>
									)
								})}
							</Stack>
						</Box>
					</Box>

					<Box
						flex={{ md: "none", lg: "1" }}
						display="flex"
						flexDirection="col"
						height={{ xs: "auto", lg: "full" }}
						width={"full"}
					>
						{/* Scrollable Area */}
						<Box flex="1" minHeight="0" overflowY="auto" paddingRight="4">
							{!selectedSectionFilter ||
							selectedSectionFilter === "section-hotels" ? (
								<Stack gap="2">
									<Inline
										id="section-hotels"
										alignItems="center"
										fontSize="lg"
										fontWeight="semibold"
										color="default"
										borderBottomWidth="1"
										borderColor="default"
										position="sticky"
										top="0"
										zIndex="10"
										backgroundColor="default"
										rounded="lg"
										padding={"2"}
										gap="2"
									>
										<Inline
											alignItems="center"
											justifyContent="center"
											padding="2"
											borderRadius="md"
											bgColor="primary"
										>
											<Icons.Bed color={"primary"} size="5" />
										</Inline>
										<Text as="h3" fontSize={"md"}>
											{selectedSectionFilter === "section-hotels"
												? "Only Hotels With Package"
												: "Hotels Stay Options"}
										</Text>
									</Inline>
									<Box>
										{displayedHotels.length > 0 ? (
											<Stack gap="6">
												{displayedHotels.map((hotelItem, idx) => (
													<ReadOnlyHotelCard
														key={`hotel-${idx}`}
														hotelItem={hotelItem}
														startDate={startDate}
														trip={trip}
														badgeLabel={
															hotelItem.optionName || "Primary Option"
														}
													/>
												))}
											</Stack>
										) : (
											<ItineraryCardSkeleton />
										)}
									</Box>
								</Stack>
							) : null}

							{(!selectedSectionFilter ||
								selectedSectionFilter.startsWith("section-day-")) &&
							combinedServices.length > 0 ? (
								<Stack gap="4">
									<Inline
										alignItems="center"
										fontSize="lg"
										fontWeight="semibold"
										color="default"
										borderBottomWidth="1"
										borderColor="default"
										position="sticky"
										top="0"
										zIndex="10"
										backgroundColor="default"
										rounded="lg"
										padding={"3"}
										gap="2"
									>
										<Inline
											alignItems="center"
											justifyContent="center"
											padding="2"
											borderRadius="md"
											bgColor="primary"
										>
											<Icons.Bus color={"primary"} size="5" />
										</Inline>
										<Text as="h3" fontSize={"md"}>
											Transportation & Activities{" "}
											{selectedSectionFilter &&
											selectedSectionFilter.startsWith("section-day-")
												? `- Day ${selectedSectionFilter.split("-")[2]}`
												: ""}
										</Text>
									</Inline>
									<Box>
										<Stack gap="6">
											{combinedServices
												.filter((item) => {
													if (!selectedSectionFilter) return true
													const targetDay = parseInt(
														selectedSectionFilter.split("-")[2]
													)
													const itemDay = item.date
														? getRelativeDayNum(item.date, startDate)
														: -1
													return itemDay === targetDay
												})
												.map((item, idx, filteredArr) => {
													const dayNum = item.date
														? getRelativeDayNum(item.date, startDate)
														: -1
													const isFirstOfDay =
														idx === 0 ||
														(filteredArr[idx - 1].date &&
															getRelativeDayNum(
																filteredArr[idx - 1].date as string,
																startDate
															) !== dayNum)

													const content =
														item.type === "cab" ? (
															<ReadOnlyTransportCard
																key={`cab-${idx}`}
																cabItem={item.data}
																startDate={startDate}
																badgeLabel="Transport"
															/>
														) : (
															<ReadOnlyActivityCard
																key={`act-${idx}`}
																actItem={item.data}
																startDate={startDate}
																badgeLabel="Activity"
															/>
														)

													return (
														<Box
															key={`${item.type}-${idx}`}
															id={
																isFirstOfDay && dayNum > 0
																	? `section-day-${dayNum}`
																	: undefined
															}
															width="full"
														>
															{content}
														</Box>
													)
												})}
										</Stack>
									</Box>
								</Stack>
							) : null}
						</Box>
						{/* AI Current Action Status Bar — at bottom of right content */}
						<Inline
							gap="4"
							alignItems="center"
							backgroundColor="white"
							borderRadius="lg"
							borderWidth="1"
							borderColor="muted"
							padding="3"
							paddingX="2"
							flexShrink="0"
						>
							<Inline
								alignItems="center"
								justifyContent="center"
								width="8"
								height="8"
								borderRadius="lg"
								backgroundColor="primary"
								color="primary"
								flexShrink="0"
							>
								<Box className={styles.actionIconSvg}>
									<Icons.Refresh spin size="4" />
								</Box>
							</Inline>
							<Stack gap="1" flexGrow="1">
								<Text
									as="span"
									fontSize="xs"
									fontWeight="semibold"
									color="primary"
									style={{
										letterSpacing: "0.05em",
										textTransform: "uppercase",
									}}
								>
									AI STATUS
								</Text>
								<Inline alignItems="center" gap="2">
									<Text
										as="p"
										fontSize="sm"
										fontWeight="semibold"
										color="default"
										key={
											streamLabel ||
											progressChecklistItems.find(
												(i) => i.subtitle === "Pending"
											)?.title ||
											"Completing"
										}
										className={styles.actionText}
									>
										{streamLabel ||
											progressChecklistItems.find(
												(i) => i.subtitle === "Pending"
											)?.title ||
											"Completing"}
									</Text>
								</Inline>
							</Stack>
						</Inline>
					</Box>
				</Box>
			</Stack>
		</Box>
	)
}

function DateTag({
	date,
	renderDate,
}: {
	date?: string | string[] | Date | Date[] | null
	renderDate?: (date: string | Date) => React.ReactNode
}) {
	const dates = date ? (Array.isArray(date) ? date : [date]) : []
	return (
		<Stack gap="2">
			<Inline flexWrap={"wrap"}>
				{dates && dates.length > 0 ? (
					joinAttributes(
						...dates.map((d, idx) => (
							<Inline
								flexWrap={"wrap"}
								key={idx}
								borderRadius="full"
								fontSize="xs"
								alignItems="center"
								backgroundColor={"default"}
							>
								<Text fontSize="xs" color={"muted"}>
									{renderDate ? renderDate(d) : String(d)}
								</Text>
							</Inline>
						))
					)
				) : (
					<Box
						height="4"
						borderRadius="sm"
						backgroundColor="subtle"
						width="full"
					/>
				)}
			</Inline>
		</Stack>
	)
}

function SetAttribute({
	prop,
	children,
	title,
	maxWidth,
}: {
	prop: React.ReactNode
	children: React.ReactNode
	title?: string
	maxWidth?: React.ComponentProps<typeof Box>["maxWidth"]
}) {
	const textContent =
		title || (typeof children === "string" ? children : undefined)
	return (
		<Stack gap="2" maxWidth={maxWidth}>
			<Box
				fontSize="xs"
				letterSpacing="wider"
				fontWeight="semibold"
				color="muted"
				whiteSpace="preserve"
			>
				{prop}
			</Box>
			<Text
				whiteSpace={maxWidth ? undefined : "nowrap"}
				textOverflow={maxWidth ? "truncate" : undefined}
				title={textContent}
				fontSize="sm"
				fontWeight="semibold"
			>
				{children}
			</Text>
		</Stack>
	)
}

function ReadOnlyHotelCard({
	hotelItem,
	startDate,
	trip,
	badgeLabel,
}: {
	hotelItem: IQuoteHotelParams[number]
	startDate: Date
	trip: ITrip
	badgeLabel: string
}) {
	return (
		<Inline
			gap="4"
			backgroundColor="white"
			borderRadius="lg"
			padding="4"
			borderWidth="1"
			borderColor="default"
			collapseBelow="sm"
			alignItems="start"
		>
			{hotelItem.hotel.thumb_image_url ? (
				<Box
					as="img"
					src={hotelItem.hotel.thumb_image_url}
					alt="Hotel Image"
					borderWidth="1"
					rounded="md"
					style={{ width: "100px", height: "57px", objectFit: "cover" }}
					flexShrink="0"
				/>
			) : (
				<Box
					style={{ width: "100px", height: "57px" }}
					display="flex"
					justifyContent="center"
					alignItems="center"
					bgColor="inset"
					as="label"
					htmlFor="file"
					rounded="md"
					flexShrink="0"
				>
					<Icons.Photograph />
				</Box>
			)}
			<Stack gap="1" width="full" flex="1" minWidth="0">
				<Inline
					gap="4"
					collapseBelow="md"
					alignItems={{ xs: "start", md: "center" }}
				>
					<Stack gap="1">
						<Inline gap="1" flexWrap="wrap" alignItems="center">
							<Text fontSize="base" fontWeight="semibold" color="default">
								{hotelItem.hotel?.name}
							</Text>
							<Badge status="accent" outlined>
								<Text fontSize={"xs"}>{badgeLabel}</Text>
							</Badge>
						</Inline>
						<Inline gap={"2"} alignItems={"center"}>
							<Text fontSize="xs" color="subtle">
								{`${(hotelItem.hotel as { location?: { name: string } }).location?.name || trip.destinations?.[0]?.name} • ${hotelItem.hotel?.stars_string} `}
							</Text>
						</Inline>
					</Stack>
					<Inline gap="2" flexWrap="wrap">
						<SetAttribute prop={"Rooms"}>
							{hotelItem.no_of_rooms} {hotelItem.room_type?.name}
						</SetAttribute>
						{hotelItem.meal_plan?.name ? (
							<SetAttribute prop={"Meal Plan"}>
								{hotelItem.meal_plan?.name}
							</SetAttribute>
						) : null}

						{hotelItem.persons_per_room ? (
							<SetAttribute prop={"Base Pax"}>
								{hotelItem.persons_per_room}
							</SetAttribute>
						) : null}
						{hotelItem.adults_with_extra_bed ? (
							<SetAttribute prop={"AWEB"}>
								{hotelItem.adults_with_extra_bed}
							</SetAttribute>
						) : null}
						{hotelItem.children_with_extra_bed ? (
							<SetAttribute prop={"CWEB"}>
								{hotelItem.children_with_extra_bed}
							</SetAttribute>
						) : null}
						{hotelItem.children_without_extra_bed ? (
							<SetAttribute prop={"CNB"}>
								{hotelItem.children_without_extra_bed}
							</SetAttribute>
						) : null}
					</Inline>
				</Inline>
				<DateTag
					date={hotelItem.dates}
					renderDate={(d) => getRelativeDayLabel(parseDate(d), startDate, "N")}
				/>
			</Stack>
		</Inline>
	)
}
function ItineraryCardSkeleton() {
	return (
		<Stack
			gap="2"
			backgroundColor="white"
			borderRadius="lg"
			padding="6"
			borderWidth="1"
			borderColor="default"
			className={styles.skeletonPulse}
		>
			<Inline gap="2" alignItems="start">
				<Inline
					alignItems="center"
					justifyContent="center"
					backgroundColor="subtle"
					color="transparent"
					padding="2"
					borderRadius="lg"
				>
					<Box style={{ width: "24px", height: "24px" }} />
				</Inline>
				<Stack gap="1">
					<Box
						as="span"
						height="3"
						borderRadius="sm"
						backgroundColor="subtle"
						style={{ width: "100px", marginTop: "4px" }}
					/>
					<Box
						as="span"
						height="5"
						borderRadius="sm"
						backgroundColor="subtle"
						style={{ width: "200px", marginTop: "4px" }}
					/>
					<Box
						as="span"
						height="3"
						borderRadius="sm"
						backgroundColor="subtle"
						style={{ width: "150px", marginTop: "4px" }}
					/>
				</Stack>
			</Inline>

			<Box
				display="flex"
				flexDirection={{ xs: "col", md: "row" }}
				gap="2"
				alignItems="start"
			>
				<Stack gap="2" width={{ xs: "full", md: "1/4" }} flexShrink="0">
					<Box as="label" fontSize="xs" fontWeight="semibold" color="default">
						Stay Nights
					</Box>
					<Stack gap="2" alignItems="start">
						<Box
							height="8"
							borderRadius="md"
							backgroundColor="subtle"
							width="full"
						/>
						<Box
							height="8"
							borderRadius="md"
							backgroundColor="subtle"
							width="full"
						/>
					</Stack>
				</Stack>
				<Stack gap="4" flex="1" minWidth="0">
					{/* Row 1: Hotel, Meal Plan, Room Type */}
					<Grid gap="2">
						<Col xs={12} md={5}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									Hotel
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={12} md={3}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									Meal Plan
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={12} md={4}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									Room Type
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
					</Grid>
					<Grid gap="4">
						<Col xs={6} md={2}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									Pax/room{" "}
									<Box as="span" color="muted">
										(WoEB)
									</Box>
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={6} md={2}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									No. of rooms
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={6} md={2}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									AWEB
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={6} md={2}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									CWEB
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={6} md={2}>
							<Stack gap="1">
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									CNB
								</Box>
								<Box
									height="10"
									borderRadius="md"
									backgroundColor="subtle"
									width="full"
								/>
							</Stack>
						</Col>
						<Col xs={12} md={2}>
							<Stack
								gap="0"
								alignItems="end"
								justifyContent="end"
								height="full"
								paddingBottom="2"
							>
								<Box
									as="label"
									fontSize="xs"
									fontWeight="semibold"
									color="default"
								>
									Comp Child
								</Box>
								<Box
									as="span"
									fontSize="sm"
									fontWeight="semibold"
									color="default"
								>
									Upto 5y (0C)
								</Box>
							</Stack>
						</Col>
					</Grid>
				</Stack>
			</Box>
		</Stack>
	)
}

function ReadOnlyTransportCard({
	cabItem,
	startDate,
	badgeLabel,
}: {
	cabItem: Partial<IQuoteCab>
	startDate: Date
	badgeLabel: string
}) {
	const ts = cabItem.transport_service
	if (!ts) return false
	return (
		<Inline
			gap="4"
			backgroundColor="white"
			borderRadius="lg"
			padding="4"
			borderWidth="1"
			borderColor="default"
			collapseBelow="sm"
			alignItems="start"
		>
			{ts.thumb_image_url ? (
				<Box
					as="img"
					src={ts.thumb_image_url}
					alt="Service Image"
					borderWidth="1"
					rounded="md"
					style={{ width: "100px", height: "63px", objectFit: "cover" }}
					flexShrink="0"
				/>
			) : (
				<Box
					style={{ width: "100px", height: "63px" }}
					display="flex"
					justifyContent="center"
					alignItems="center"
					bgColor="inset"
					as="label"
					htmlFor="file"
					rounded="md"
					flexShrink="0"
				>
					<Icons.Photograph />
				</Box>
			)}
			<Stack gap="1" width="full" flex="1" minWidth="0">
				<Inline
					gap="4"
					collapseBelow="md"
					alignItems={{ xs: "start", md: "center" }}
				>
					<Stack gap="1">
						<Text fontSize="base" fontWeight="semibold" color="default">
							{ts.name}
						</Text>
						<Box>
							<Badge status="accent" outlined>
								<Text fontSize={"xs"}>{badgeLabel}</Text>
							</Badge>
						</Box>
					</Stack>
					<Inline gap="2" flexWrap="wrap">
						<SetAttribute prop={"Service Type"}>
							{ts.service_display_name}
							{ts.duration_string ? ` (Duration: ${ts.duration_string})` : ""}
						</SetAttribute>
						{ts.start_time ? (
							<SetAttribute prop={"Start Time"}>{ts.start_time}</SetAttribute>
						) : null}
						{ts.duration ? (
							<SetAttribute prop={"Duration"}>
								{`${ts.duration} Mins`}
							</SetAttribute>
						) : null}
						<SetAttribute prop={"Transportation"}>
							{cabItem?.cab_type?.name} Qty: {cabItem.no_of_cabs || 1}
							{cabItem?.cab_type?.capacity
								? `, Cap: ${cabItem.cab_type.capacity} Pax`
								: null}
						</SetAttribute>
					</Inline>
				</Inline>
				<DateTag
					date={cabItem.date}
					renderDate={(d) =>
						getRelativeDayLabel(parseDate(d), startDate, "Day")
					}
				/>
			</Stack>
		</Inline>
	)
}

function ReadOnlyActivityCard({
	actItem,
	startDate,
	badgeLabel,
}: {
	actItem: IQuoteTravelActivity
	startDate: Date
	badgeLabel: string
}) {
	const act = actItem.activity

	return (
		<Inline
			gap="4"
			backgroundColor="white"
			borderRadius="lg"
			padding="4"
			borderWidth="1"
			borderColor="default"
			collapseBelow="sm"
			alignItems="start"
		>
			{act.thumb_image_url ? (
				<Box
					as="img"
					src={act.thumb_image_url}
					alt="Activity Image"
					borderWidth="1"
					rounded="md"
					style={{ width: "100px", height: "63px", objectFit: "cover" }}
					flexShrink="0"
				/>
			) : (
				<Box
					style={{ width: "100px", height: "63px" }}
					display="flex"
					justifyContent="center"
					alignItems="center"
					bgColor="inset"
					as="label"
					htmlFor="file"
					rounded="md"
					flexShrink="0"
				>
					<Icons.Photograph />
				</Box>
			)}
			<Stack gap="1" width="full" flex="1" minWidth="0">
				<Inline
					gap="4"
					collapseBelow="md"
					alignItems={{ xs: "start", md: "center" }}
				>
					<Stack gap="1">
						<Text fontSize="base" fontWeight="semibold" color="default">
							{act.name}
						</Text>
						<Box>
							<Badge status="accent" outlined>
								<Text fontSize={"xs"}>{badgeLabel}</Text>
							</Badge>
						</Box>
					</Stack>
					<Inline gap="2" flexWrap="wrap">
						<SetAttribute
							maxWidth="lg"
							title={actItem.ticket_type?.name}
							prop={"Ticket/Package Type"}
						>
							{actItem.ticket_type?.name}
						</SetAttribute>
						<SetAttribute prop={"Slot"}>{actItem.slot}</SetAttribute>
						{actItem.duration ? (
							<SetAttribute prop={"Duration"}>{actItem.duration}</SetAttribute>
						) : null}
						{actItem.ticket_tourist_configurations ? (
							<SetAttribute prop={"Tickets"}>
								{actItem.ticket_tourist_configurations
									.map(
										(c) =>
											`${c.configuration?.name || "Adult"} (Qty: ${c.quantity || 1})`
									)
									.join(", ")}
							</SetAttribute>
						) : null}
					</Inline>
				</Inline>
				<DateTag
					date={actItem.date}
					renderDate={(d) =>
						getRelativeDayLabel(parseDate(d), startDate, "Day")
					}
				/>
			</Stack>
		</Inline>
	)
}
